import { Stack } from "@mui/system";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { useChangePassword } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { func } from "prop-types";
import { PasswordInput } from "../FormFields";
import { ForgotPasswordLink, PasswordValidator } from "./";

interface ChangePasswordFormProps {
  handleClose: () => void;
}

export const ChangePasswordForm = (props: ChangePasswordFormProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  //region React Hook (useState)
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [passwordValid, setPasswordValid] = useState(false);
  const [removeTokens, setRemoveTokens] = useState(false);
  //endregion React Hook (useState)

  //region React Hook (useEffect)
  const handleCurrentPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCurrentPassword(event.target.value);
  };
  const handleNewPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewPassword(event.target.value);
    if (confirmPassword && confirmPassword !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }

    if (
      (confirmPassword && confirmPassword !== event.target.value) ||
      !passwordValid
    ) {
      setSaveButtonDisabled(true);
    } else {
      if (confirmPassword) {
        setSaveButtonDisabled(false);
      }
    }
  };
  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setConfirmPassword(event.target.value);
    if (newPassword !== event.target.value) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }

    if (newPassword !== event.target.value || !passwordValid) {
      setSaveButtonDisabled(true);
    } else {
      if (event.target.value) {
        setSaveButtonDisabled(false);
      }
    }
  };
  //endregion React Hook (useEffect)

  //region Handlers
  const handleClose = () => {
    props.handleClose();
  };

  const handleIsValid = (isValid: boolean) => {
    setPasswordValid(isValid);
  };

  const handleRemoveTokens = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRemoveTokens(event.target.checked);
  };

  const { changePassword, loading } = useChangePassword(() => {
    handleClose();
    enqueueSnackbar(gettext("Your password has been changed"), {
      variant: "success",
    });
  });

  const handleButtonClick = () => {
    const variables = {
      currentPassword,
      newPassword,
      removeTokens,
    };
    changePassword({
      variables,
    }).then();
  };
  //endregion Handlers

  return (
    <Grid
      container
      spacing={2}
      sx={{
        height: "100%",
      }}
    >
      <Grid size={12}>
        <Stack>
          <PasswordInput
            placeholder={gettext("Current Password")}
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
          />
        </Stack>
        <ForgotPasswordLink />
      </Grid>
      <Grid size={12}>
        <Stack>
          <PasswordInput
            placeholder={gettext("New Password")}
            value={newPassword}
            onChange={handleNewPasswordChange}
          />
        </Stack>
        <PasswordValidator
          password={newPassword}
          handleIsValid={handleIsValid}
        />
      </Grid>
      <Grid size={12}>
        <Stack>
          <PasswordInput
            placeholder={gettext("Confirm Password")}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
          />
        </Stack>
        {confirmPasswordError && (
          <FormHelperText error id="accountId-error">
            {gettext("Confirm password does not match.")}
          </FormHelperText>
        )}
      </Grid>
      <Grid size={12}>
        <FormControlLabel
          control={<Checkbox onChange={handleRemoveTokens} />}
          label={gettext(
            "Require all devices to sign in again with the new password.",
          )}
          sx={{
            color: "grey.600",
          }}
        />
      </Grid>

      <Grid size={12} sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          loading={loading}
          fullWidth
          onClick={handleButtonClick}
          disabled={saveButtonDisabled}
        >
          {gettext("Change Password")}
        </Button>
      </Grid>
    </Grid>
  );
};

ChangePasswordForm.propTypes = {
  handleClose: func.isRequired,
};
